import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CreateIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {CreateIssueFieldsSection} from '@github-ui/issue-metadata/FieldsSection'
import {CreateIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'
import {CreateIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {CreateIssueProjectsSection} from '@github-ui/issue-metadata/ProjectsSection'
import {CreateIssueRelationshipsSection} from '@github-ui/issue-metadata/RelationshipsSection'
import {CreateIssueIssueTypesSection} from '@github-ui/issue-metadata/TypesSection'

import {useIssueCreateConfigContext} from './../contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from './../contexts/IssueCreateDataContext'
import type {SharedMetadataProps} from './MetadataSelectors'
import styles from './MetadataSidebar.module.css'

export const MetadataSidebar = ({
  canSetAssignees,
  canSetLabels,
  canSetMilestone,
  canSetProjects,
  canSetType,
  canSetIssueFields,
  canSetRelationships,
  issueTypesEnabled,
  orgPinnedFields,
  isPinnedFieldsLoading,
}: SharedMetadataProps) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {
    repository,
    labels,
    setLabels,
    assignees,
    setAssignees,
    projects,
    setProjects,
    milestone,
    setMilestone,
    issueType,
    setIssueType,
    setIssueFields,
    parentIssue,
    setParentIssue,
    blockedByIssues,
    setBlockedByIssues,
    blockingIssues,
    setBlockingIssues,
  } = useIssueCreateDataContext()

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
  }

  if (!repository) {
    return null
  }

  return (
    <div className={styles.MetadataSidebarContainer}>
      <h2 className="sr-only">Metadata</h2>
      {repository && (
        <CreateIssueAssigneesSection
          repo={repository.name}
          owner={repository.owner.login}
          readonly={!canSetAssignees}
          assignees={assignees}
          onSelectionChange={setAssignees}
          className={styles.CreateIssueAssigneesSection}
          repository={repository}
          issueNumber={undefined} // Not available in create context
          onIssueUpdate={undefined} // Not applicable in create context
          {...sharedConfigProps}
        />
      )}
      <CreateIssueLabelsSection
        repo={repository.name}
        owner={repository.owner.login}
        readonly={!canSetLabels}
        labels={labels}
        onSelectionChange={setLabels}
        {...sharedConfigProps}
      />
      {issueTypesEnabled && (
        <CreateIssueIssueTypesSection
          owner={repository.owner.login}
          repo={repository.name}
          {...sharedConfigProps}
          viewerCanType={canSetType}
          type={issueType}
          onSelectionChange={setIssueType}
        />
      )}
      {canSetIssueFields && (
        <CreateIssueFieldsSection
          owner={repository.owner.login}
          ownerId={repository.owner.databaseId}
          onSelectionChange={setIssueFields}
          issueType={issueType}
          orgPinnedFields={orgPinnedFields}
          isPinnedFieldsLoading={isPinnedFieldsLoading}
          {...sharedConfigProps}
        />
      )}
      <CreateIssueProjectsSection
        owner={repository.owner.login}
        repo={repository.name}
        projects={projects}
        readonly={!canSetProjects}
        onSelectionChange={setProjects}
        {...sharedConfigProps}
      />
      <CreateIssueMilestonesSection
        repo={repository.name}
        owner={repository.owner.login}
        milestone={milestone}
        onSelectionChange={setMilestone}
        viewerCanSetMilestone={canSetMilestone ?? false}
        className={styles.CreateIssueAssigneesSection}
        {...sharedConfigProps}
      />
      {isFeatureEnabled('issue_create_relationships') && (
        <CreateIssueRelationshipsSection
          owner={repository.owner.login}
          repo={repository.name}
          readonly={!canSetRelationships}
          parentIssue={parentIssue}
          onParentChange={setParentIssue}
          blockedByIssues={blockedByIssues}
          onBlockedByChange={setBlockedByIssues}
          blockingIssues={blockingIssues}
          onBlockingChange={setBlockingIssues}
        />
      )}
    </div>
  )
}

try{ MetadataSidebar.displayName ||= 'MetadataSidebar' } catch {}